import React from 'react';
import { useNavigate } from 'react-router-dom';

import styles from 'prototypes/securedCard/app.module.scss';
import AppChrome from 'prototypes/securedCard/components/AppChrome';
import RateChips from 'prototypes/securedCard/components/RateChips';
import StepFooter from 'prototypes/securedCard/components/StepFooter';
import { formatMoney } from 'prototypes/securedCard/formatMoney';
import { PrototypePath } from 'prototypes/securedCard/paths';
import { CASHBACK_LABEL, SAVINGS_APY_LABEL } from 'prototypes/securedCard/prototypeAssumptions';
import { cadencePhrase } from 'prototypes/securedCard/storyMath';
import { useStory } from 'prototypes/securedCard/StoryContext';

const KeepStep = () => {
  const navigate = useNavigate();
  const { state, projection, finish } = useStory();

  return (
    <AppChrome
      step={PrototypePath.Keep}
      title="What you actually get"
      subtitle="The paycheck is still yours. Part of it is ready to spend, the card paid itself, and both interest and cashback show up."
    >
      <div className={styles.stack}>
        <RateChips />
        <div className={styles.figureList}>
          <div className={styles.figure}>
            <div>
              <p className={styles.figureLabel}>Still saved</p>
              <p className={styles.figureHint}>After the grocery run was paid</p>
            </div>
            <p className={styles.figureValue}>{formatMoney(projection.savingsAfterPaymentCents)}</p>
          </div>
          <div className={`${styles.figure} ${styles.figurePrimary}`}>
            <div>
              <p className={styles.figureLabel}>Still on the card</p>
              <p className={styles.figureHint}>Ready for the next purchase</p>
            </div>
            <p className={styles.figureValue}>{formatMoney(projection.availableAfterPaymentCents)}</p>
          </div>
          <div className={styles.figure}>
            <div>
              <p className={styles.figureLabel}>Interest earned</p>
              <p className={styles.figureHint}>
                {SAVINGS_APY_LABEL} on the savings. Kept separate from the paycheck split.
              </p>
            </div>
            <p className={styles.figureValue}>{formatMoney(projection.interestCents)}</p>
          </div>
          <div className={styles.figure}>
            <div>
              <p className={styles.figureLabel}>Cashback</p>
              <p className={styles.figureHint}>{CASHBACK_LABEL} on the grocery run</p>
            </div>
            <p className={styles.figureValue}>{formatMoney(projection.cashbackCents)}</p>
          </div>
        </div>
        <div className={`${styles.callout} ${styles.calloutGreen}`}>
          <p className={styles.calloutTitle}>
            Next payday adds {formatMoney(state.perPaycheckCents)} {cadencePhrase(state.cadence)}
          </p>
          <p className={styles.calloutBody}>
            Savings become {formatMoney(projection.nextSavingsCents)}. About{' '}
            {formatMoney(projection.nextPaycheckAvailableGainCents)} of that lands on the card, taking it to{' '}
            {formatMoney(projection.nextAvailableCents)}. The limit grows because you keep getting paid.
          </p>
        </div>
      </div>
      <StepFooter
        backTo={PrototypePath.Spend}
        nextLabel="See the card"
        onNext={() => {
          finish();
          navigate(PrototypePath.Dashboard);
        }}
      />
    </AppChrome>
  );
};

export default KeepStep;
